import polylabel from '@mapbox/polylabel'
import { GeoJSONSource, LayerSpecification, LineLayerSpecification } from 'mapbox-gl'
import mapboxgl from 'mapbox-gl'
import React, { useCallback, useEffect, useReducer, useRef, useMemo } from 'react'

import { GeoPoint } from 'utils/mapUtils'

import config from '../../app/config'
import { getColorForAllocation } from '../../utils/allocationColors'
import { CreateGeoJsonPoints } from './GeoJsonPoints'
import { isWebGLSupported } from './isWebGLSupported'

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default

export interface LayerReference {
  sourceId: string
  layerIds: string[]
  type: 'polygon' | 'point'
}

export type MapState = 'uninitialized' | 'creating' | 'loading-resources' | 'ready' | 'error' | 'unsupported'

export interface MapStateContext {
  state: MapState
  error?: Error
  loadingResources: Set<string>
  map?: mapboxgl.Map
}

export type MapAction = { type: 'CREATE_MAP'; payload: { container: HTMLDivElement; styleUrl?: string } } | { type: 'MAP_CREATED'; payload: { map: mapboxgl.Map } } | { type: 'RESOURCE_LOADING'; payload: { resource: string } } | { type: 'RESOURCE_LOADED'; payload: { resource: string } } | { type: 'RESOURCE_ERROR'; payload: { resource: string; error: Error } } | { type: 'MAP_READY' } | { type: 'ERROR'; payload: { error: Error } } | { type: 'UNSUPPORTED' } | { type: 'RESET' }

export interface QueuedOperation {
  id: string
  operation: () => void | Promise<void>
  validate?: () => boolean
}

export interface EventRegistration {
  target: mapboxgl.Map
  type: string
  listener: any
}

export interface MapBoxPlaceFeature {
  id: string
  type: string
  geometry: {
    type: string
    coordinates: [number, number]
  }
}

export interface MapBoxPlaceResponse {
  attribution: string
  features: MapBoxPlaceFeature[]
  type: string
}

export interface PointLayerOptions {
  clusterSettings?: {
    cluster: boolean
    maxzoom?: number
    radius?: number
    properties?: Record<string, any>
  }
  clusterOptions?: Partial<LayerSpecification>
  unclusteredOptions?: Partial<LayerSpecification>
}

const calculatePolygonArea = (polygon: number[][][]) => {
  const ring = polygon[0]
  let area = 0
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length
    area += ring[i][0] * ring[j][1]
    area -= ring[j][0] * ring[i][1]
  }
  return Math.abs(area / 2)
}

const getBoundingBox = (polygon: number[][][]) => {
  const ring = polygon[0]
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity
  for (const [x, y] of ring) {
    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}

const InitialMapCenter: mapboxgl.LngLatLike = [-98.5795, 39.8283]
const InitialMapZoom = 3

const initialMapState: MapStateContext = {
  state: 'uninitialized',
  loadingResources: new Set()
}

const mapStateReducer = (state: MapStateContext, action: MapAction): MapStateContext => {
  switch (action.type) {
    case 'CREATE_MAP':
      return {
        ...state,
        state: 'creating',
        error: undefined
      }

    case 'MAP_CREATED':
      return {
        ...state,
        state: 'loading-resources',
        map: action.payload.map,
        loadingResources: new Set(['billboard-icon'])
      }

    case 'RESOURCE_LOADING':
      return {
        ...state,
        loadingResources: new Set([...state.loadingResources, action.payload.resource])
      }

    case 'RESOURCE_LOADED':
      const newLoadingResources = new Set(state.loadingResources)
      newLoadingResources.delete(action.payload.resource)

      return {
        ...state,
        loadingResources: newLoadingResources,
        state: newLoadingResources.size === 0 ? 'ready' : state.state
      }

    case 'RESOURCE_ERROR':
      const errorLoadingResources = new Set(state.loadingResources)
      errorLoadingResources.delete(action.payload.resource)

      return {
        ...state,
        loadingResources: errorLoadingResources,
        state: errorLoadingResources.size === 0 ? 'ready' : state.state,
        error: action.payload.error
      }

    case 'MAP_READY':
      return {
        ...state,
        state: 'ready'
      }

    case 'ERROR':
      return {
        ...state,
        state: 'error',
        error: action.payload.error
      }

    case 'UNSUPPORTED':
      return {
        ...state,
        state: 'unsupported'
      }

    case 'RESET':
      return initialMapState

    default:
      return state
  }
}

export const useMapBox = () => {
  const [mapStateContext, dispatch] = useReducer(mapStateReducer, initialMapState)
  const [center, setCenter] = React.useState<GeoPoint>()
  const [zoom, setZoom] = React.useState<number>()
  const [initialCenter, setInitialCenter] = React.useState<[number, number] | null>(null)
  const [initialZoom, setInitialZoom] = React.useState<number | null>(null)

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const markers = useRef<any[]>([])
  const layersRef = useRef<Map<string, LayerReference>>(new Map())
  const zipCodesWithDataRef = useRef<Set<string>>(new Set())
  const operationQueue = useRef<QueuedOperation[]>([])
  const eventRegistry = useRef<EventRegistration[]>([])
  const layerConfigCache = useRef<Map<string, any>>(new Map())
  const tilesetLoadingStateRef = useRef<Map<string, boolean>>(new Map())
  const retryTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const accessToken = config.mapBox.token

  const isReady = mapStateContext.state === 'ready'
  const isLoading = mapStateContext.state === 'creating' || mapStateContext.state === 'loading-resources'
  const hasError = mapStateContext.state === 'error'
  const isUnsupported = mapStateContext.state === 'unsupported'
  const mapBoxRef = useRef<mapboxgl.Map | undefined>()

  useEffect(() => {
    mapBoxRef.current = mapStateContext.map
  }, [mapStateContext.map])

  const registerEventListener = useCallback((target: mapboxgl.Map, type: string, listener: any) => {
    const existingRegistration = eventRegistry.current.find(reg => reg.target === target && reg.type === type && reg.listener === listener)

    if (!existingRegistration) {
      target.on(type, listener)
      eventRegistry.current.push({ target, type, listener })
    }
  }, [])

  const cleanupEventListeners = useCallback(() => {
    eventRegistry.current.forEach(({ target, type, listener }) => {
      try {
        target.off(type, listener)
      } catch (error) {
        console.warn('Failed to remove event listener:', error)
      }
    })
    eventRegistry.current = []
  }, [])

  const queueOperation = useCallback(
    (operation: QueuedOperation) => {
      if (isReady && (!operation.validate || operation.validate())) {
        try {
          const result = operation.operation()
          if (result instanceof Promise) {
            result.catch(error => console.error('Queued operation failed:', error))
          }
        } catch (error) {
          console.error('Queued operation failed:', error)
        }
      } else {
        operationQueue.current.push(operation)
      }
    },
    [isReady]
  )

  const executeQueuedOperations = useCallback(() => {
    const operations = operationQueue.current
    operationQueue.current = []

    operations.forEach(operation => {
      if (!operation.validate || operation.validate()) {
        try {
          const result = operation.operation()
          if (result instanceof Promise) {
            result.catch(error => console.error('Queued operation failed:', error))
          }
        } catch (error) {
          console.error('Queued operation failed:', error)
        }
      }
    })
  }, [])

  const loadBillboardIcon = useCallback(async (map: mapboxgl.Map) => {
    try {
      dispatch({ type: 'RESOURCE_LOADING', payload: { resource: 'billboard-icon' } })

      const response = await fetch('/Billboard.svg')
      if (!response.ok) {
        throw new Error(`Failed to fetch billboard icon: ${response.status}`)
      }

      const svgText = await response.text()
      const blob = new Blob([svgText], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)

      return new Promise<void>((resolve, reject) => {
        const image = new Image()
        image.onload = () => {
          try {
            if (map && !map.hasImage('billboard')) {
              map.addImage('billboard', image)
            }
            URL.revokeObjectURL(url)
            dispatch({ type: 'RESOURCE_LOADED', payload: { resource: 'billboard-icon' } })
            resolve()
          } catch (error) {
            URL.revokeObjectURL(url)
            dispatch({ type: 'RESOURCE_ERROR', payload: { resource: 'billboard-icon', error: error as Error } })
            reject(error)
          }
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          const error = new Error('Failed to load billboard image')
          dispatch({ type: 'RESOURCE_ERROR', payload: { resource: 'billboard-icon', error } })
          reject(error)
        }
        image.src = url
      })
    } catch (error) {
      dispatch({ type: 'RESOURCE_ERROR', payload: { resource: 'billboard-icon', error: error as Error } })
      throw error
    }
  }, [])

  const removeLayerById = useCallback((id: string) => {
    if (!mapBoxRef.current || !layersRef.current.has(id)) {
      return
    }

    const layerRef = layersRef.current.get(id)!

    layerRef.layerIds.forEach(layerId => {
      if (mapBoxRef.current && mapBoxRef.current.getLayer(layerId)) {
        mapBoxRef.current.removeLayer(layerId)
      }
    })

    if (mapBoxRef.current.getSource(layerRef.sourceId)) {
      mapBoxRef.current.removeSource(layerRef.sourceId)
    }

    layersRef.current.delete(id)
  }, [])

  const initializeMap = useCallback(
    async (container: HTMLDivElement, styleUrl?: string) => {
      if (mapStateContext.state !== 'uninitialized') {
        return
      }

      if (!isWebGLSupported()) {
        dispatch({ type: 'UNSUPPORTED' })
        return
      }

      try {
        dispatch({ type: 'CREATE_MAP', payload: { container, styleUrl } })

        mapboxgl.accessToken = accessToken

        const map = new mapboxgl.Map({
          container,
          style: styleUrl,
          config: {
            basemap: {
              colorPlaceLabels: 'hsl(0, 0%, 0%)',
              showLandmarkIcons: false,
              show3dObjects: false,
              colorGreenspace: 'hsl(115, 20%, 84%)',
              densityPointOfInterestLabels: 3,
              showPlaceLabels: true,
              theme: 'default',
              colorModePointOfInterestLabels: 'single',
              font: 'Inter',
              showPointOfInterestLabels: false,
              colorAdminBoundaries: 'hsl(345, 71%, 52%)',
              colorWater: 'hsl(200, 72%, 88%)',
              lightPreset: 'day',
              showTransitLabels: true,
              showPedestrianRoads: true,
              showRoadLabels: true
            }
          },
          center: InitialMapCenter,
          zoom: InitialMapZoom
        })

        map.scrollZoom.disable()
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false, showZoom: true, visualizePitch: false }), 'top-right')

        dispatch({ type: 'MAP_CREATED', payload: { map } })

        dispatch({ type: 'RESOURCE_LOADING', payload: { resource: 'map-style' } })

        const moveHandler = () => {
          const mapCenter = map.getCenter()
          const mapZoom = map.getZoom()
          setCenter({ lat: mapCenter.lat, lng: mapCenter.lng })
          setZoom(mapZoom)
        }

        registerEventListener(map, 'load', async () => {
          try {
            await loadBillboardIcon(map)
          } catch (error) {
            console.warn('Failed to load billboard icon:', error)
          }
        })

        map.once('style.load', () => {
          dispatch({ type: 'RESOURCE_LOADED', payload: { resource: 'map-style' } })
        })

        registerEventListener(map, 'move', moveHandler)
      } catch (error) {
        dispatch({ type: 'ERROR', payload: { error: error as Error } })
        throw error
      }
    },
    [accessToken, registerEventListener, loadBillboardIcon, mapStateContext.state]
  )

  useEffect(() => {
    if (isReady) {
      executeQueuedOperations()
    }
  }, [isReady, executeQueuedOperations])

  useEffect(() => {
    return () => {
      cleanupEventListeners()

      const currentMap = mapBoxRef.current
      const container = mapContainerRef.current

      if (currentMap) {
        try {
          layersRef.current.forEach(layerRef => {
            layerRef.layerIds.forEach(layerId => {
              if (currentMap.getLayer && currentMap.getLayer(layerId)) {
                currentMap.removeLayer(layerId)
              }
            })
            if (currentMap.getSource && currentMap.getSource(layerRef.sourceId)) {
              currentMap.removeSource(layerRef.sourceId)
            }
          })

          currentMap.remove()
        } catch (error) {
          console.warn('Error during map cleanup:', error)
        } finally {
          layersRef.current.clear()
          layerConfigCache.current.clear()
          operationQueue.current = []

          retryTimersRef.current.forEach(timer => clearTimeout(timer))
          retryTimersRef.current.clear()
          tilesetLoadingStateRef.current.clear()

          mapBoxRef.current = undefined
        }
      }

      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild)
        }
      }

      dispatch({ type: 'RESET' })
    }
  }, [cleanupEventListeners])

  const flyTo = useCallback((center: [number, number], zoom: number, speed?: number) => {
    if (!mapBoxRef.current) {
      return
    }

    let flyToOptions: any = { center, zoom }

    if (speed !== undefined) {
      flyToOptions.speed = speed
    }

    mapBoxRef.current.flyTo(flyToOptions)
  }, [])

  const jumpTo = useCallback((center: [number, number], zoom: number) => {
    if (!mapBoxRef.current) {
      return
    }

    mapBoxRef.current.jumpTo({ center, zoom })
  }, [])

  const setInitialCenterAndZoom = useCallback(
    (center: [number, number], zoom: number) => {
      setInitialCenter(center)
      setInitialZoom(zoom)
      jumpTo(center, zoom)
    },
    [jumpTo]
  )

  const returnToCenter = useCallback(() => {
    if (initialCenter && initialZoom !== null) {
      flyTo(initialCenter, initialZoom)
    }
  }, [initialCenter, initialZoom, flyTo])

  const searchPlace = useCallback(
    async (place: string) => {
      const requestUrl = `https://api.mapbox.com/search/geocode/v6/forward?q=${place}&country=US&access_token=${accessToken}`
      const response = await fetch(requestUrl).then(response => {
        return response.json()
      })
      return response as MapBoxPlaceResponse
    },
    [accessToken]
  )

  const clearMarkers = useCallback(() => {
    if (!markers.current) {
      return
    }

    markers.current.forEach(marker => {
      marker.remove()
    })
    markers.current = []
  }, [])

  const addPolygonLayer = useCallback(
    (id: string, data: GeoJSON.FeatureCollection, options?: LineLayerSpecification) => {
      const configKey = `polygon-${id}`
      const currentConfig = { data, options }
      const cachedConfig = layerConfigCache.current.get(configKey)

      if (cachedConfig && JSON.stringify(cachedConfig) === JSON.stringify(currentConfig)) {
        return
      }

      layerConfigCache.current.set(configKey, currentConfig)

      const operation: QueuedOperation = {
        id: `add-polygon-layer-${id}`,
        operation: () => {
          if (!mapBoxRef.current) return

          removeLayerById(id)

          const sourceId = `${id}-source`

          mapBoxRef.current.addSource(sourceId, {
            type: 'geojson',
            data
          })

          const fillLayerId = `${id}-fill`
          mapBoxRef.current.addLayer({
            id: fillLayerId,
            type: 'fill',
            source: sourceId,
            paint: options?.paint || {
              'fill-color': '#088',
              'fill-opacity': 0
            }
          })

          const layerIds = [fillLayerId]

          if (options?.paint) {
            const outlineLayerId = `${id}-outline`
            mapBoxRef.current.addLayer({
              id: outlineLayerId,
              type: 'line',
              source: sourceId,
              paint: {
                'line-color': options.paint['line-color'],
                'line-width': options.paint['line-width']
              }
            })
            layerIds.push(outlineLayerId)
          }

          layersRef.current.set(id, {
            sourceId,
            layerIds,
            type: 'polygon'
          })
        },
        validate: () => !!mapBoxRef.current
      }

      queueOperation(operation)
    },
    [queueOperation, removeLayerById]
  )

  const addPointLayer = useCallback(
    async (id: string, data: GeoJSON.FeatureCollection, options?: PointLayerOptions) => {
      const operation: QueuedOperation = {
        id: `add-point-layer-${id}`,
        operation: () => {
          if (!mapBoxRef.current) return

          removeLayerById(id)

          const sourceId = `${id}-source`
          const defaultClusterOptions = {
            cluster: true,
            clusterMaxZoom: 13,
            clusterRadius: 50
          }

          const sourceOptions = {
            type: 'geojson' as const,
            data,
            ...(options?.clusterSettings?.cluster
              ? {
                  cluster: options?.clusterSettings?.cluster ?? defaultClusterOptions.cluster,
                  clusterMaxZoom: options?.clusterSettings?.maxzoom ?? defaultClusterOptions.clusterMaxZoom,
                  clusterRadius: options?.clusterSettings?.radius ?? defaultClusterOptions.clusterRadius,
                  ...(options?.clusterSettings?.properties && { clusterProperties: options.clusterSettings.properties })
                }
              : {})
          }

          mapBoxRef.current.addSource(sourceId, sourceOptions)

          const layerIds: string[] = []

          if (options?.clusterSettings?.cluster) {
            const clustersLayerId = `${id}-clusters`

            if (options?.clusterOptions) {
              mapBoxRef.current.addLayer({ ...options.clusterOptions, id: clustersLayerId, source: sourceId } as LayerSpecification)
            } else {
              mapBoxRef.current.addLayer({
                id: clustersLayerId,
                type: 'circle',
                source: sourceId,
                filter: ['has', 'point_count'],
                paint: {
                  'circle-color': ['step', ['get', 'point_count'], '#d95f0e', 50, '#d95f0e'],
                  'circle-radius': ['step', ['get', 'point_count'], 15, 2, 20, 5, 25, 10, 30],
                  'circle-opacity': 0.6
                }
              })
            }

            layerIds.push(clustersLayerId)
          }

          const unclusteredLayerId = `${id}-unclustered`

          if (options?.unclusteredOptions) {
            mapBoxRef.current.addLayer({ ...options.unclusteredOptions, id: unclusteredLayerId, source: sourceId, filter: ['!', ['has', 'point_count']] } as LayerSpecification)
          } else {
            mapBoxRef.current.addLayer({
              id: unclusteredLayerId,
              type: 'circle',
              source: sourceId,
              filter: ['!', ['has', 'point_count']],
              paint: {
                'circle-color': '#26374d',
                'circle-radius': 5,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#fff'
              }
            })
          }

          layerIds.push(unclusteredLayerId)

          layersRef.current.set(id, {
            sourceId,
            layerIds,
            type: 'point'
          })
        },
        validate: () => !!mapBoxRef.current
      }

      queueOperation(operation)
    },
    [queueOperation, removeLayerById]
  )

  const setMarkers = useCallback(
    async (points: GeoPoint[], options?: PointLayerOptions) => {
      if (points.length === 0) {
        return
      }

      const geoJsonPoints = CreateGeoJsonPoints(points)
      await addPointLayer('points', geoJsonPoints.data, options)
    },
    [addPointLayer]
  )

  const waitForLayer = useCallback(async (layerId: string): Promise<boolean> => {
    if (!mapBoxRef.current) {
      return false
    }

    return new Promise(resolve => {
      const checkLayer = () => {
        if (mapBoxRef.current && mapBoxRef.current.getLayer(layerId)) {
          resolve(true)
        } else {
          setTimeout(checkLayer, 100)
        }
      }
      checkLayer()
    })
  }, [])

  const setFeatureState = useCallback(
    (layerId: string, featureId: string | number, state: any) => {
      const operation: QueuedOperation = {
        id: `set-feature-state-${layerId}-${featureId}`,
        operation: () => {
          if (!mapBoxRef.current) return
          mapBoxRef.current.setFeatureState({ source: layerId, id: featureId }, state)
        },
        validate: () => !!mapBoxRef.current
      }

      queueOperation(operation)
    },
    [queueOperation]
  )

  const findZipCodeLayer = useCallback(() => {
    if (!mapBoxRef.current) {
      return null
    }

    const style = mapBoxRef.current.getStyle()
    const layers = style?.layers || []

    return layers.find((layer: any) => {
      return layer.id === 'georef-united-states-of-ameri-abxmld-fill'
    })
  }, [])

  const waitForZipCodeLayer = useCallback(
    async (maxAttempts: number = 10): Promise<any> => {
      if (!mapBoxRef.current) return null

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const layer = findZipCodeLayer()
        if (layer) return layer

        await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)))
      }

      console.warn('Zip code layer not found after', maxAttempts, 'attempts')
      return null
    },
    [findZipCodeLayer]
  )

  const waitForTilesetReady = useCallback(
    async (sourceId: string, maxWaitTime: number = 5000): Promise<boolean> => {
      if (!mapBoxRef.current) return false

      const map = mapBoxRef.current
      const startTime = Date.now()
      const checkInterval = 100

      const checkTilesetReady = async (): Promise<boolean> => {
        if (Date.now() - startTime > maxWaitTime) {
          console.warn(`Tileset ${sourceId} not ready after ${maxWaitTime}ms`)
          return false
        }

        try {
          if (map.isSourceLoaded && map.isSourceLoaded(sourceId)) {
            if (map.areTilesLoaded && map.areTilesLoaded()) {
              return true
            }
          }

          const source = map.getSource(sourceId)
          if (!source) {
            await new Promise(resolve => setTimeout(resolve, checkInterval))
            return checkTilesetReady()
          }

          const layer = findZipCodeLayer()
          if (layer && layer.source === sourceId) {
            try {
              const features = map.querySourceFeatures(sourceId, {
                sourceLayer: layer['source-layer']
              })
              if (features && features.length > 0) {
                tilesetLoadingStateRef.current.set(sourceId, true)
                return true
              }
            } catch (error) {}
          }

          await new Promise(resolve => setTimeout(resolve, checkInterval))
          return checkTilesetReady()
        } catch (error) {
          console.warn('Error checking tileset ready state:', error)
          await new Promise(resolve => setTimeout(resolve, checkInterval))
          return checkTilesetReady()
        }
      }

      return checkTilesetReady()
    },
    [findZipCodeLayer]
  )

  const getZipCodeBoundary = useCallback(
    async (zipCode: string) => {
      if (!mapBoxRef.current) {
        return null
      }

      const zipCodeLayer = await waitForZipCodeLayer()
      if (!zipCodeLayer?.source) {
        return null
      }

      try {
        let allFeatures = mapBoxRef.current.querySourceFeatures(zipCodeLayer.source, {
          sourceLayer: zipCodeLayer['source-layer']
        })

        let matchingFeatures: any[] = []
        if (allFeatures && allFeatures.length > 0) {
          matchingFeatures = allFeatures.filter((feature: any) => {
            const featureZipcode = feature.properties?.zcta5_code
            if (featureZipcode) {
              const cleanZipcode = featureZipcode.replace(/^\["?/, '').replace(/"?\]$/, '')
              return cleanZipcode === zipCode
            }
            return false
          })
        }

        if (matchingFeatures.length === 0) {
          const placeResponse = await searchPlace(zipCode)
          if (placeResponse?.features?.length > 0) {
            const feature = placeResponse.features[0]

            await new Promise(resolve => {
              mapBoxRef.current!.once('moveend', resolve)
              mapBoxRef.current!.flyTo({
                center: [feature.geometry.coordinates[0], feature.geometry.coordinates[1]],
                zoom: 9
              })
            })

            const newAllFeatures = mapBoxRef.current.querySourceFeatures(zipCodeLayer.source, {
              sourceLayer: zipCodeLayer['source-layer']
            })

            if (newAllFeatures && newAllFeatures.length > 0) {
              const newMatchingFeatures = newAllFeatures.filter((feature: any) => {
                const featureZipcode = feature.properties?.zcta5_code
                if (featureZipcode) {
                  const cleanZipcode = featureZipcode.replace(/^\["?/, '').replace(/"?\]$/, '')
                  return cleanZipcode === zipCode
                }
                return false
              })

              if (newMatchingFeatures.length > 0) {
                matchingFeatures.length = 0
                matchingFeatures.push(...newMatchingFeatures)
              } else {
                return null
              }
            } else {
              return null
            }
          } else {
            return null
          }
        }

        const features = matchingFeatures

        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity

        features.forEach((feature: any) => {
          const processCoordinates = (coords: number[][]) => {
            coords.forEach(([lng, lat]) => {
              if (lng < minX) minX = lng
              if (lng > maxX) maxX = lng
              if (lat < minY) minY = lat
              if (lat > maxY) maxY = lat
            })
          }

          if (feature.geometry.type === 'Polygon') {
            feature.geometry.coordinates.forEach((ring: number[][]) => {
              processCoordinates(ring)
            })
          } else if (feature.geometry.type === 'MultiPolygon') {
            feature.geometry.coordinates.forEach((polygon: number[][][]) => {
              polygon.forEach((ring: number[][]) => {
                processCoordinates(ring)
              })
            })
          }
        })

        if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
          return null
        }

        return {
          bbox: { minX, minY, maxX, maxY }
        }
      } catch (error) {
        return null
      }
    },
    [waitForZipCodeLayer, searchPlace]
  )

  const fitToZipCodeBounds = useCallback(
    async (zipCode: string) => {
      if (!mapBoxRef.current) {
        return false
      }

      const result = await getZipCodeBoundary(zipCode)
      if (!result) {
        return false
      }

      const { bbox } = result
      const centerLng = (bbox.minX + bbox.maxX) / 2
      const centerLat = (bbox.minY + bbox.maxY) / 2
      const width = bbox.maxX - bbox.minX
      const height = bbox.maxY - bbox.minY
      const size = Math.max(width, height)

      let zoomLevel
      if (size > 0.3) {
        zoomLevel = 8
      } else if (size > 0.15) {
        zoomLevel = 9
      } else if (size > 0.08) {
        zoomLevel = 10
      } else {
        zoomLevel = 11
      }

      mapBoxRef.current.flyTo({
        center: [centerLng, centerLat],
        zoom: zoomLevel,
        duration: 1000
      })

      return true
    },
    [getZipCodeBoundary]
  )

  const addZipCodeLabels: any = useCallback(
    async (removeOnIdle: boolean = false, retryCount: number = 0) => {
      const operation: QueuedOperation = {
        id: 'add-zipcode-labels',
        operation: async () => {
          if (!mapBoxRef.current) return

          const zipCodeLayer = await waitForZipCodeLayer()
          const labelLayerId = 'zipcode-labels'
          const labelSourceId = 'zipcode-labels-source'

          if (!zipCodeLayer) {
            if (mapBoxRef.current.getLayer(labelLayerId)) {
              mapBoxRef.current.removeLayer(labelLayerId)
            }
            if (mapBoxRef.current.getSource(labelSourceId)) {
              mapBoxRef.current.removeSource(labelSourceId)
            }

            return
          }

          const minzoom = zipCodeLayer.minzoom ?? 8
          const maxzoom = zipCodeLayer.maxzoom ?? 22
          const currentZoom = mapBoxRef.current.getZoom()

          if (currentZoom < minzoom || currentZoom > maxzoom) {
            return
          }

          const hasLabelLayer = mapBoxRef.current.getLayer(labelLayerId)
          const hasLabelSource = mapBoxRef.current.getSource(labelSourceId)

          if (!zipCodeLayer.source) {
            return
          }

          const sourceId = zipCodeLayer.source
          const isTilesetReady = tilesetLoadingStateRef.current.get(sourceId)

          if (!isTilesetReady) {
            const isReady = await waitForTilesetReady(sourceId, 3000)
            if (!isReady && retryCount < 3) {
              const delay = Math.pow(2, retryCount) * 1000

              const timerId = setTimeout(() => {
                retryTimersRef.current.delete('add-zipcode-labels-retry')
                addZipCodeLabels(removeOnIdle, retryCount + 1)
              }, delay)

              retryTimersRef.current.set('add-zipcode-labels-retry', timerId)
              return
            }
            if (!isReady) {
              console.warn('Tileset not ready after retries, skipping label addition')
              return
            }
          }

          try {
            const features = mapBoxRef.current.querySourceFeatures(zipCodeLayer.source, {
              sourceLayer: zipCodeLayer['source-layer']
            })

            if (!features || features.length <= 0) {
              if (retryCount < 2) {
                tilesetLoadingStateRef.current.set(sourceId, false)
                const delay = Math.pow(2, retryCount) * 500

                const timerId = setTimeout(() => {
                  retryTimersRef.current.delete('add-zipcode-labels-retry-nofeatures')
                  addZipCodeLabels(removeOnIdle, retryCount + 1)
                }, delay)

                retryTimersRef.current.set('add-zipcode-labels-retry-nofeatures', timerId)
              }
              return
            }

            const polygonsByZipcode = new Map<string, any[]>()
            features.forEach((feature: any) => {
              const zipcode = feature.properties?.zcta5_code
              if (zipcode) {
                const cleanZipcode = zipcode.replace(/^\["?/, '').replace(/"?\]$/, '')
                if (!polygonsByZipcode.has(cleanZipcode)) {
                  polygonsByZipcode.set(cleanZipcode, [])
                }
                if (feature.geometry.type === 'Polygon') {
                  polygonsByZipcode.get(cleanZipcode)!.push(feature.geometry.coordinates)
                } else if (feature.geometry.type === 'MultiPolygon') {
                  feature.geometry.coordinates.forEach((polygon: any) => {
                    polygonsByZipcode.get(cleanZipcode)!.push(polygon)
                  })
                }
              }
            })

            const labelFeatures: any[] = []
            polygonsByZipcode.forEach((geometries, zipcode) => {
              if (zipCodesWithDataRef.current.has(zipcode)) {
                if (geometries.length === 0) {
                  return
                }

                const polygonsWithMeta = geometries.map(geom => ({
                  geometry: geom,
                  area: calculatePolygonArea(geom),
                  bbox: getBoundingBox(geom)
                }))

                polygonsWithMeta.sort((a, b) => b.area - a.area)

                const largestPolygon = polygonsWithMeta[0]
                labelFeatures.push({
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: polylabel(largestPolygon.geometry, 1.0)
                  },
                  properties: {
                    zipcode: zipcode
                  }
                })
              }
            })

            if (hasLabelSource) {
              const source = mapBoxRef.current.getSource(labelSourceId) as GeoJSONSource

              if (source) {
                source.setData({
                  type: 'FeatureCollection',
                  features: labelFeatures
                })
              }
            } else {
              mapBoxRef.current.addSource(labelSourceId, {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: labelFeatures
                }
              })
            }

            if (!hasLabelLayer) {
              mapBoxRef.current.addLayer({
                id: labelLayerId,
                type: 'symbol',
                source: labelSourceId,
                minzoom: minzoom,
                maxzoom: maxzoom,
                layout: {
                  'text-field': ['get', 'zipcode'],
                  'text-font': ['Inter Medium'],
                  'text-size': 12,
                  'text-allow-overlap': false,
                  'text-ignore-placement': false
                },
                paint: {
                  'text-color': '#000000',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 2,
                  'text-opacity': 1.0
                }
              })
            }

            if (removeOnIdle) {
              mapBoxRef.current.off('idle', onIdle)
            }
          } catch (error) {
            console.error('Failed to add zipcode labels:', error)
          }
        },
        validate: () => !!mapBoxRef.current && isReady
      }

      queueOperation(operation)
    },
    [queueOperation, waitForZipCodeLayer, waitForTilesetReady, isReady]
  )

  const onIdle = useCallback(
    (e: any) => {
      const zipCodeLayer = findZipCodeLayer()
      if (!zipCodeLayer) {
        return
      }

      if (e && e.sourceId === zipCodeLayer.source) {
        addZipCodeLabels(true)
      }
    },
    [findZipCodeLayer, addZipCodeLabels]
  )

  const updateTilesetLayer = useCallback(
    async (allocationMap: Map<string, number>) => {
      const operation: QueuedOperation = {
        id: 'update-tileset-layer',
        operation: async () => {
          zipCodesWithDataRef.current = new Set(allocationMap.keys())
          if (!mapBoxRef.current) return

          const layer = await waitForZipCodeLayer()
          if (!layer) {
            console.warn('Cannot update tileset layer - zip code layer not found')
            return
          }

          const lookupExpression: any = ['case']
          allocationMap.forEach((allocation, zipCode) => {
            lookupExpression.push(['==', ['get', 'zcta5_code'], JSON.stringify([zipCode])])
            lookupExpression.push(getColorForAllocation(allocation))
          })
          lookupExpression.push('transparent')

          const opacityExpression: any = ['case']
          allocationMap.forEach((_, zipCode) => {
            opacityExpression.push(['==', ['get', 'zcta5_code'], JSON.stringify([zipCode])])
            opacityExpression.push(0.7)
          })
          opacityExpression.push(0)

          try {
            mapBoxRef.current.setPaintProperty(layer.id, 'fill-color', lookupExpression)
            mapBoxRef.current.setPaintProperty(layer.id, 'fill-opacity', opacityExpression)

            tilesetLoadingStateRef.current.set(layer.source, false)

            const sourceDataHandler = (e: any) => {
              if (e.sourceId === layer.source && e.isSourceLoaded) {
                tilesetLoadingStateRef.current.set(layer.source, true)
                mapBoxRef.current!.off('sourcedata', sourceDataHandler)
                addZipCodeLabels()
              }
            }

            registerEventListener(mapBoxRef.current, 'sourcedata', sourceDataHandler)
            registerEventListener(mapBoxRef.current, 'idle', onIdle)

            if (tilesetLoadingStateRef.current.get(layer.source)) {
              addZipCodeLabels()
            }
          } catch (error) {
            console.error('Failed to update paint property:', error)
          }
        },
        validate: () => !!mapBoxRef.current && isReady
      }

      queueOperation(operation)
    },
    [queueOperation, waitForZipCodeLayer, registerEventListener, onIdle, addZipCodeLabels, isReady]
  )

  useEffect(() => {
    if (isReady && mapBoxRef.current) {
      const map = mapBoxRef.current

      const handleDragEnd = () => addZipCodeLabels()
      const handleZoomEnd = () => addZipCodeLabels()

      registerEventListener(map, 'dragend', handleDragEnd)
      registerEventListener(map, 'zoomend', handleZoomEnd)

      addZipCodeLabels()
    }
  }, [isReady, registerEventListener, addZipCodeLabels])

  const mapBoxRefForContainer = useMemo(() => ({ current: mapContainerRef.current }), [])

  return {
    isReady,
    isLoading,
    hasError,
    isUnsupported,
    error: mapStateContext.error,
    mapBoxRef: mapBoxRefForContainer,
    mapInstance: mapBoxRef,
    mapContainerRef,
    flyTo,
    jumpTo,
    setInitialCenterAndZoom,
    returnToCenter,
    searchPlace,
    center,
    zoom,
    setMarkers,
    clearMarkers,
    addPolygonLayer,
    addPointLayer,
    updateTilesetLayer,
    addZipCodeLabels,
    setFeatureState,
    waitForLayer,
    hasInitialCenter: initialCenter !== null,
    initializeMap,
    getZipCodeBoundary,
    fitToZipCodeBounds
  }
}
